//import "@/styles/globals.css";
import "../lib/date-extension";
import "../lib/yup-extension";
import type { AppProps } from "next/app";
import type { NextPage } from "next";
import {
  ReactElement,
  ReactNode,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  ChakraProvider,
  createMultiStyleConfigHelpers,
  defineStyle,
  extendTheme,
} from "@chakra-ui/react";
import "../styles/globals.css";
import { RecoilEnv, RecoilRoot } from "recoil";
import useMock from "@/hooks/use-mock";
import Fonts from "@/features/ui/fonts";
import "../features/line-sheet-sets/app-date-picker.css";
import "../scripts/wdyr";
import { isProduction } from "@/shared/utils/environment";
import { Helmet, HelmetProvider } from "react-helmet-async";
import "../features/landing/landing-superset.scss";
import "../i18n";
import "../sentry";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Inter } from "next/font/google";
import { isMobile } from "react-device-detect";
import * as amplitude from "@amplitude/analytics-browser";
import { switchTheme } from "@/shared/components/currency-switch";
import {
  focusManager,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { SWRConfig } from "swr";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useRouter } from "next/router";
import { touchTokenCookies } from "@/utils/token-cookie";
import { endSession, isSessionEndedError } from "@/utils/token-refresher";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { checkboxAnatomy } from "@chakra-ui/anatomy";
import { accordionTheme } from "@/features/my-order/features/order-history/components/OrderHistoryAccordion";
import CenteredSpinner from "@/shared/components/centered-spinner";
import { getCanonicalUrl } from "@/shared/utils/canonical-url";
import { SITE_NAME } from "@/shared/constants/site";

const inter = Inter({ subsets: ["latin"] });

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const sizes = {
  container: {
    "2xl": "1920px",
  },
};

const fonts = {
  heading: `${inter.style.fontFamily}, 'Noto Sans CJK KR', sans-serif`,
  body: `${inter.style.fontFamily}, 'Noto Sans CJK KR', sans-serif`,
};

const breakpoints = {
  sm: "320px",
  md: "768px",
  lg: "1366px",
  xl: "1920px",
};

const shadows = {
  baseBoxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.10)",
  hairlineDefault: "0 0 0 0.5px #8F8F8C",
  hairlineSubtle: "0 0 0 0.5px #D4D4D1",
  hairlineHover: "0 0 0 0.5px #1272EF",
  hairlineActive: "0 0 0 0.5px #00417D",
};

const textStyles = {
  "heading-3xl": {
    fontSize: "60px",
    fontWeight: "bold",
    lineHeight: "80px",
    letterSpacing: 0,
  },
  "heading-2xl": {
    fontSize: "48px",
    fontWeight: "bold",
    lineHeight: "68px",
    letterSpacing: 0,
  },
  "heading-xl": {
    fontSize: "36px",
    fontWeight: "bold",
    lineHeight: "48px",
    letterSpacing: 0,
  },
  "heading-lg": {
    fontSize: "28px",
    fontWeight: "bold",
    lineHeight: "40px",
    letterSpacing: 0,
  },
  "heading-md": {
    fontSize: "24px",
    fontWeight: "bold",
    lineHeight: "36px",
    letterSpacing: 0,
  },
  "heading-sm": {
    fontSize: "20px",
    fontWeight: "bold",
    lineHeight: "28px",
    letterSpacing: 0,
  },
  "body-lg-bold": {
    fontSize: "16px",
    fontWeight: "bold",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-lg-semi-bold": {
    fontSize: "16px",
    fontWeight: "600",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-lg-medium": {
    fontSize: "16px",
    fontWeight: "500",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-lg-regular": {
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-md-bold": {
    fontSize: "14px",
    fontWeight: "bold",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-md-semi-bold": {
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-md-medium": {
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-md-regular": {
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-sm-bold": {
    fontSize: "13px",
    fontWeight: "bold",
    lineHeight: "20px",
    letterSpacing: 0,
  },
  "body-sm-medium": {
    fontSize: "13px",
    fontWeight: "500",
    lineHeight: "20px",
    letterSpacing: 0,
  },
  "body-sm-regular": {
    fontSize: "13px",
    fontWeight: "400",
    lineHeight: "20px",
    letterSpacing: 0,
  },
  "caption-bold": {
    fontSize: "12px",
    fontWeight: "bold",
    lineHeight: "16px",
    letterSpacing: 0,
  },
  "caption-medium": {
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "16px",
    letterSpacing: 0,
  },
  "caption-regular": {
    fontSize: "12px",
    fontWeight: "400",
    lineHeight: "16px",
    letterSpacing: 0,
  },
  "body-md-underline": {
    fontSize: "14px",
    fontWeight: "bold",
    lineHeight: "24px",
    letterSpacing: 0,
  },
  "body-sm-underline": {
    fontSize: "13px",
    fontWeight: "bold",
    lineHeight: "20px",
    letterSpacing: 0,
  },
  "caption-underline": {
    fontSize: "12px",
    fontWeight: "bold",
    lineHeight: "16px",
    letterSpacing: 0,
  },
};

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(checkboxAnatomy.keys);

const sj_design = definePartsStyle({
  control: defineStyle({
    rounded: "4px",
    ring: "none",
    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.10)",
    border: "1px solid #D4D4D1",
    width: "20px",
    height: "20px",
    _checked: {
      border: "1px solid #1272EF",
      background: "#FFFFFF",
      _hover: {
        background: "#EFEFED",
      },
    },
    _hover: {
      background: "#EFEFED",
    },
    _focus: {
      boxShadow: "none",
    },
  }),
  icon: defineStyle({
    color: "#1272EF",
  }),
});

export const checkboxTheme = defineMultiStyleConfig({
  variants: {
    sj_design,
  },
});

const theme = extendTheme({
  sizes,
  fonts,
  textStyles,
  breakpoints,
  shadows,
  styles: {
    global: {
      body: {
        bg: "#FCFCFE",
      },
    },
  },
  components: {
    Switch: switchTheme,
    Checkbox: checkboxTheme,
    Accordion: accordionTheme,
  },
});

function SWRProvider({ children }: { children: React.ReactNode }) {
  const { error: showError } = useAppToasts();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  return (
    <SWRConfig value={{ onError: (err) => showErrorRef.current(err) }}>
      {children}
    </SWRConfig>
  );
}

// react-query 경로(axios-client)는 SWR의 useFetcher를 타지 않아 세션 종료 처리가 없었다.
// suspense가 아닌 useQuery/useMutation은 AppErrorFallback에도 걸리지 않아서,
// 갱신이 끝내 실패한 뒤에는 401 토스트만 반복해서 떴다.
function createQueryClient() {
  const handleAuthError = (error: unknown) => {
    if (isSessionEndedError(error)) {
      endSession();
    }
  };

  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
    queryCache: new QueryCache({ onError: handleAuthError }),
    mutationCache: new MutationCache({ onError: handleAuthError }),
  });
}

let resetMeta = false;

RecoilEnv.RECOIL_DUPLICATE_ATOM_KEY_CHECKING_ENABLED = false;

// 앱 루트 에러 폴백. 토큰 만료 등으로 suspense 데이터 페치가 throw할 때 잡아준다.
// (지금까지는 최상위 Suspense만 있고 ErrorBoundary가 없어 throw 시 트리 전체가 언마운트돼
//  흰 화면이 됐고, 새로고침해야 복구됐다.) 인증 에러면 로그인으로, 그 외엔 복구 UI를 보여준다.
function AppErrorFallback({ error }: FallbackProps) {
  useEffect(() => {
    const anyErr = error as any;
    const code = anyErr?.error?.code ?? anyErr?.response?.status ?? anyErr?.code;
    const status = anyErr?.error?.status;
    const isAuthError =
      code === 401 ||
      code === 403 ||
      status === "UNAUTHENTICATED" ||
      status === "JWT_TOKEN_REFRESH_FAILED" ||
      status === "JWT_TOKEN_EXPIRED" ||
      status === "JWT_TOKEN_INVALIDATED";
    if (isAuthError) {
      endSession();
    }
  }, [error]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        justifyContent: "center",
        alignItems: "center",
        height: "100dvh",
        width: "100dvw",
      }}
    >
      <div>일시적인 문제가 발생했어요.</div>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: "8px 16px",
          borderRadius: "6px",
          border: "1px solid #D4D4D1",
          cursor: "pointer",
        }}
      >
        새로고침
      </button>
    </div>
  );
}

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const { i18n } = useTranslation();
  const { t } = useI18nHelper();
  const [shouldRender] = useMock();
  const [render, setRender] = useState(false);
  const router = useRouter();
  // 렌더마다 새로 만들면 캐시·중복제거가 매번 버려져, 백그라운드에서 복귀할 때
  // 같은 요청이 무더기로 다시 나간다(만료 직후라면 401 폭풍이 된다).
  const [queryClient] = useState(createQueryClient);

  // link를 공유했을 때 미리보기에서 meta 정보가 보이게하려면(사이트 제목, 설명, 이미지를 크롤링할 수 있게 만들어주려면) _document.tsx에서 미리 meta를 넣어두어야 함.
  // _document.tsx에 넣어두어야 Next.js가 index.ts.html 을 만들 때 meta를 같이 넣어주고, 그러면 공유한 서비스에서 단순 크롤링 시 index.ts.html의 meta 정보를 가져갈 수 있음.
  // 그러나 HelmetProvider 안에 들어가지 않은 meta는 Helmet이 관리해주지 않음.
  // 따라서, 좋은 방법은 아니지만 _document.tsx 에서 설정한 meta를 지우고 Helmet의 관리하에 다시 넣어주는 방식으로 해결.
  // SSR을 하면 이 방식은 필요하지 않을 것 같음.
  const canUseDOM = typeof window !== "undefined";
  if (canUseDOM && !resetMeta) {
    const head = window.document.getElementsByTagName("head");
    if (head) {
      head[0]?.querySelector("[name='viewport']")?.remove();
      head[0]?.querySelector("[name='description']")?.remove();
      head[0]?.querySelector("[property='og:site_name']")?.remove();
      head[0]?.querySelector("[property='og:locale']")?.remove();
      head[0]?.querySelector("[property='og:title']")?.remove();
      head[0]?.querySelector("[property='og:description']")?.remove();
      head[0]?.querySelector("[property='og:url']")?.remove();
      head[0]?.querySelector("[property='og:image']")?.remove();
      head[0]?.querySelector("[property='og:image:secure_url']")?.remove();
      head[0]?.querySelector("[property='og:image:type']")?.remove();
      head[0]?.querySelector("[property='og:image:width']")?.remove();
      head[0]?.querySelector("[property='og:image:height']")?.remove();
      head[0]?.querySelector("[property='og:image:alt']")?.remove();
      head[0]?.querySelector("[property='twitter:title']")?.remove();
      head[0]?.querySelector("[property='twitter:description']")?.remove();
      head[0]?.querySelector("[property='twitter:url']")?.remove();
      head[0]?.querySelector("[property='twitter:image']")?.remove();
      resetMeta = true;
    }
  }

  useEffect(() => {
    // GTM(_document.tsx)과 같은 기준을 쓴다. 예전에는 Amplitude 는 호스트로,
    // GTM 은 NEXT_PUBLIC_API_URL 로 게이트해서 로컬에서 한쪽만 로드되는 비대칭이 있었다.
    if (isProduction()) {
      amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY!);
    }
  }, []);

  // iOS 사파리의 input 포커스 자동 확대를 막는다. 실제 폰트 규칙은 globals.css의
  // body.is-mobile-device 블록에 있고, 여기서는 그 스코프만 켜준다.
  // 레이아웃 분기(private-layout의 BrowserView/MobileView)와 같은 판별식을 써야
  // "모바일 레이아웃인데 폰트는 데스크톱" 불일치가 생기지 않는다.
  // isMobile은 모듈 로드 시 UA로 확정되는 상수이므로 의존성 배열은 비운다.
  useEffect(() => {
    if (!isMobile) {
      return;
    }

    document.body.classList.add("is-mobile-device");

    return () => {
      document.body.classList.remove("is-mobile-device");
    };
  }, []);

  // 설치된 PWA의 로그인 유지. 토큰 쿠키를 같은 값으로 다시 심어 만료를 연장한다.
  // iOS는 백그라운드에서 복귀할 때 React를 리마운트하지 않으므로 마운트만으로는
  // 부족하다. 포그라운드 복귀마다 갱신해야 ITP의 7일 시계가 실제로 리셋된다.
  useEffect(() => {
    touchTokenCookies();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        touchTokenCookies();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    setRender(shouldRender);
  }, [shouldRender]);

  useEffect(() => {
    focusManager.setEventListener((handleFocus) => {
      if (typeof window === "undefined" || !window.addEventListener) {
        return;
      }

      const handleVisibilityChange = () => {
        handleFocus();
      };

      const handleWindowFocus = () => {
        handleFocus();
      };

      window.addEventListener(
        "visibilitychange",
        handleVisibilityChange,
        false
      );
      window.addEventListener("focus", handleWindowFocus, false);

      return () => {
        window.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("focus", handleWindowFocus);
      };
    });
  }, []);

  if (!render) {
    return null;
  }

  const getLayout = Component.getLayout ?? ((page) => page);
  return (
    <ErrorBoundary
      FallbackComponent={AppErrorFallback}
      resetKeys={[router.asPath]}
    >
      <Suspense
        fallback={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100dvh",
              width: "100dvw",
            }}
          >
            <CenteredSpinner />
          </div>
        }
      >
        <RecoilRoot>
        <HelmetProvider>
          <Helmet>
            <title>{t("common:meta.title")}</title>
            <meta
              name="viewport"
              content="width=device-width,initial-scale=1"
            />
            <meta name="description" content={t("common:meta.description")} />
            <link
              rel="canonical"
              href={getCanonicalUrl()}
            />
            <link rel="alternate" href={window.location.origin} hrefLang="en" />
            <link
              rel="alternate"
              href={`${window.location.origin}/ko`}
              hrefLang="ko"
            />
            <link rel="preload" as="image" href="/images/landing-hero.webp" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:locale" content={i18n.language} />
            <meta property="og:title" content={t("common:meta.title")} />
            <meta
              property="og:description"
              content={t("common:meta.description")}
            />
            <meta
              property="og:image"
              content={window.location.origin + "/seo-image.png"}
            />
            <meta
              property="og:image:secure_url"
              content={window.location.origin + "/seo-image.png"}
            />
            <meta property="og:image:type" content="image/png" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta
              property="og:image:alt"
              content={t("common:meta.description")}
            />
            <meta property="twitter:title" content={t("common:meta.title")} />
            <meta
              property="twitter:description"
              content={t("common:meta.description")}
            />
            <meta
              property="twitter:image"
              content={window.location.origin + "/seo-image.png"}
            />
          </Helmet>
          <QueryClientProvider client={queryClient}>
            <ChakraProvider theme={theme}>
              <SWRProvider>
                <Fonts />
                <main className={inter.className}>
                  {getLayout(<Component {...pageProps} />)}
                </main>
              </SWRProvider>
            </ChakraProvider>
          </QueryClientProvider>
        </HelmetProvider>
      </RecoilRoot>
      </Suspense>
    </ErrorBoundary>
  );
}
